import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SeeqLogo2026 } from '@/core/SeeqLogoOrange.atom';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { SimpleSaveFormBuilder } from '@/formbuilder/SimpleSaveFormBuilder.page';
import { FORM_ERROR } from '@/formbuilder/formBuilder.constants';
import { useResetPassword } from '@/main/auth/hooks/useResetPassword.hook';
import { goTo } from '@/main/routing.utilities';
import { isSelfServicePasswordResetEnabled, seeqVersion } from '@/services/systemConfiguration.utilities';
import { replaceLegacyThemeClassNames } from '@/styles/legacyTheme.utilities';
import { isCsrfSet } from '@/utilities/auth.utilities';
import { getPasswordErrorText, validPassword } from '@/utilities/password.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { setCurrentUser } from '@/workbench/workbench.actions';
import type { FormElement } from '@/formbuilder/formBuilder.constants';

export const ResetPassword = () => {
  const currentUser = useFluxPath(sqWorkbenchStore, () => sqWorkbenchStore.currentUser);

  const { t } = useTranslation();
  const {
    formData: { newPassword, passwordCheck },
    updateFormData,
    doResetPassword,
    resetPasswordError,
  } = useResetPassword();

  const [{ selfServicePasswordResetEnabled }] = useState(() => ({
    selfServicePasswordResetEnabled: isSelfServicePasswordResetEnabled(),
  }));

  const authenticated = isCsrfSet();
  if (authenticated && _.isEmpty(currentUser)) {
    setCurrentUser().catch((error) => errorToast({ httpResponseOrError: error }));
  }

  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') ?? undefined;
  const isPasswordResetForCurrentUser = () => email === currentUser.email;
  const canResetPassword = () => {
    if (!authenticated) {
      return true;
    }
    return isPasswordResetForCurrentUser();
  };

  useEffect(() => {
    document.body.className = replaceLegacyThemeClassNames(document.body.className, false, 'topic');
  }, []);

  const formDefinition: FormElement[] = [
    {
      component: 'TextFieldFormComponent',
      name: 'password',
      testId: 'password',
      label: 'USER.NEW_PASSWORD',
      placeholder: 'USER.PASSWORD_PROMPT',
      type: 'password',
      value: newPassword,
      onChange: (password) => updateFormData({ newPassword: password as string }),
      size: 'sm',
      autoComplete: 'new-password',
      required: true,
      autoFocus: true,
      validation: (newPassword: string | number) => !validPassword(newPassword as string),
      customErrorText: getPasswordErrorText(newPassword),
    },
    {
      component: 'TextFieldFormComponent',
      name: 'passwordCheck',
      testId: 'passwordCheck',
      label: 'USER.PASSWORD_CHECK',
      placeholder: 'USER.PASSWORD_CHECK_PROMPT',
      type: 'password',
      value: passwordCheck,
      onChange: (passwordCheck) => updateFormData({ passwordCheck: passwordCheck as string }),
      size: 'sm',
      autoComplete: 'new-password',
      required: true,
      validation: (passwordCheck: string | number) => (passwordCheck as string) !== newPassword,
      extendValidation: true,
      customErrorText: passwordCheck !== newPassword ? 'USER.PASSWORD_MISMATCH' : '',
    },
    {
      component: 'ErrorMessageFormComponent',
      name: 'resetPasswordError',
      testId: 'specResetPasswordError',
      type: FORM_ERROR,
      value: resetPasswordError ?? '',
      failForm: false,
      includeIf: !!resetPasswordError,
    },
  ];

  return (
    <>
      <div className="loginFooter sq-darkish-gray font-size-smaller tw:pr-sq-10 tw:pl-sq-10">
        <small>{seeqVersion()}</small>
      </div>

      <div
        data-testid="resetPasswordPage"
        className="flexRowContainer fullViewport splashScreenLight loginBackdrop flexCenter"
        style={{ backgroundImage: 'url(/login-image/), var(--sq-login-gradient)' }}>
        <div className="loginPanel loginPanelRedesigned flexRowContainer min-width-300 overlayPanel tw:w-3/8">
          <div className="loginCardFrame width-maximum">
            <div className="loginCardInner">
              <div className="tw:p-sq-40">
                <div data-testid="logoAndSlogan" className="flexRowContainer flexCenter mb25">
                  <SeeqLogo2026 />
                  <h4 className="loginSlogan mt8">{t('RESET_PASSWORD_PANEL.SLOGAN_V2')}</h4>

                  <span className="text-color-default">{t('RESET_PASSWORD_PANEL.SET_PASSWORD_PROMPT')}</span>
                </div>

                {!selfServicePasswordResetEnabled && (
                  <div className="text-center">{t('RESET_PASSWORD_PANEL.FEATURE_DISABLED')}</div>
                )}
                {selfServicePasswordResetEnabled && !canResetPassword() && (
                  <div className="text-center">
                    {t('RESET_PASSWORD_PANEL.DIFFERENT_USER_LOGGED_IN', {
                      currentEmail: currentUser.email,
                      resetEmail: email,
                    })}
                  </div>
                )}
                {selfServicePasswordResetEnabled && canResetPassword() && (
                  <SimpleSaveFormBuilder
                    testId="resetPassword"
                    formDefinition={formDefinition}
                    submitFn={doResetPassword}
                    closeFn={() => goTo('/login')}
                    submitBtnLabel="RESET_PASSWORD_PANEL.SET_PASSWORD"
                    buttonExtraClassNames="mt25 loginSubmitBtnWrapper"
                    cancelClassNames="mr20 min-width-100"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
